import React, { useEffect, useState } from "react";
import {
  UserRound,
  Mail,
  Phone,
  KeyRound,
  Save,
  Loader2,
  CheckCircle2,
  Eye,
  EyeOff,
  RefreshCw,
} from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { useCustomerAuth } from "../../../context/CustomerAuthContext/CustomerAuthContext";
import { postRequest } from "../../../api/postRequest";

/* ── Theme helpers ── */
const hexToRgb = (hex: string) => {
  const h = (hex || "#0f1115").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};
const useSurfaces = (theme?: any) => {
  const bg = theme?.backgroundColor || "#0f1115";
  const isDark = luminance(hexToRgb(bg)) < 0.5;
  return {
    bg,
    surface1: isDark ? "rgba(255,255,255,.035)" : "rgba(0,0,0,.035)",
    surface2: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)",
    surface3: isDark ? "rgba(255,255,255,.085)" : "rgba(0,0,0,.085)",
    divider: isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.12)",
    softShadow: isDark ? "0 10px 28px rgba(0,0,0,.28)" : "0 10px 28px rgba(0,0,0,.10)",
    textPri: theme?.textPrimary ?? (isDark ? "#fff" : "#111827"),
    textSec: theme?.textSecondary ?? (isDark ? "rgba(255,255,255,.7)" : "#6b7280"),
    accent: theme?.accent ?? "#22c55e",
    gradient: theme?.gradientHero ?? "linear-gradient(135deg,#2563eb,#0ea5e9)",
  };
};

type ApiRes<T> = { success: boolean; errorDetail?: string; data?: T };

type ProfilDto = {
  musteriId: number;
  ad?: string;
  soyad?: string;
  eposta?: string;
  telefon?: string;
};

/* ── Input bileşeni ── */
const InputField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  icon?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  rightElement?: React.ReactNode;
  S: ReturnType<typeof useSurfaces>;
}> = ({ label, value, onChange, placeholder, type = "text", icon, required, disabled, rightElement, S }) => (
  <label className="block">
    <span className="block text-xs mb-1" style={{ color: S.textSec }}>
      {label}{required && <span style={{ color: "#ef4444" }}> *</span>}
    </span>
    <div
      className="flex items-center gap-2 rounded-xl px-3 py-2.5 border transition"
      style={{
        backgroundColor: S.surface2,
        borderColor: S.divider,
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {icon && <span style={{ color: S.textSec }}>{icon}</span>}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1 bg-transparent outline-none text-sm"
        style={{ color: S.textPri }}
      />
      {rightElement}
    </div>
  </label>
);

/* ── Toast ── */
const Toast: React.FC<{ ok: boolean; text: string }> = ({ ok, text }) => (
  <div
    className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm border"
    style={{
      backgroundColor: ok ? "rgba(16,185,129,.12)" : "rgba(239,68,68,.10)",
      borderColor: ok ? "rgba(16,185,129,.3)" : "rgba(239,68,68,.3)",
      color: ok ? "#047857" : "#b91c1c",
    }}
  >
    {ok && <CheckCircle2 size={16} />}
    {text}
  </div>
);

/* ── Şifre gücü ── */
const PasswordStrength: React.FC<{ password: string; S: ReturnType<typeof useSurfaces> }> = ({ password, S }) => {
  if (!password) return null;
  const strength =
    password.length < 6 ? 1
    : password.length < 8 ? 2
    : /[A-Z]/.test(password) && /[0-9]/.test(password) ? 4
    : 3;
  const labels = ["", "Çok kısa", "Zayıf", "Orta", "Güçlü"];
  const colors = ["", "#ef4444", "#f59e0b", "#22c55e", "#10b981"];
  return (
    <div className="space-y-1">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((l) => (
          <div
            key={l}
            className="h-1 flex-1 rounded-full transition-colors"
            style={{ backgroundColor: l <= strength ? colors[strength] : S.surface2 }}
          />
        ))}
      </div>
      <p className="text-xs" style={{ color: S.textSec }}>{labels[strength]}</p>
    </div>
  );
};

/* ══════════════════════════════════════════════
   ANA SAYFA
══════════════════════════════════════════════ */
type TabId = "profil" | "sifre";

const ProfilePage: React.FC = () => {
  const { theme } = useTheme();
  const { customer, updateCustomerProfile, setTokenAndUserFromToken } = useCustomerAuth();
  const S = useSurfaces(theme);

  const [tab, setTab] = useState<TabId>("profil");

  /* ─── Profil state ─── */
  const [ad, setAd]           = useState("");
  const [soyad, setSoyad]     = useState("");
  const [eposta, setEposta]   = useState("");
  const [telefon, setTelefon] = useState("");

  const [profilLoading, setProfilLoading] = useState(false);
  const [fetchLoading, setFetchLoading]   = useState(false);
  const [profilMsg, setProfilMsg]         = useState<{ ok: boolean; text: string } | null>(null);

  /* ─── Şifre state ─── */
  const [mevcutSifre,     setMevcutSifre]     = useState("");
  const [yeniSifre,       setYeniSifre]       = useState("");
  const [yeniSifreTekrar, setYeniSifreTekrar] = useState("");
  const [showMevcut, setShowMevcut] = useState(false);
  const [showYeni,   setShowYeni]   = useState(false);
  const [sifreLoading, setSifreLoading] = useState(false);
  const [sifreMsg,     setSifreMsg]     = useState<{ ok: boolean; text: string } | null>(null);

  /* ─── API'dan profil çek ─── */
  const fetchProfile = async (silent = false) => {
    if (!customer?.id) return;
    if (!silent) setFetchLoading(true);
    try {
      const res = await postRequest<any, ApiRes<ProfilDto>>(
        "/MusteriAuth/profil",
        { MusteriId: customer.id }
      );
      if (res.success && res.data) {
        const d = res.data;
        setAd(d.ad ?? "");
        setSoyad(d.soyad ?? "");
        setEposta(d.eposta ?? "");
        setTelefon(d.telefon ?? "");
        /* Context'i de güncelle */
        updateCustomerProfile({
          ad: d.ad ?? "",
          soyad: d.soyad ?? "",
          email: d.eposta ?? "",
          telefon: d.telefon ?? "",
        });
      } else {
        /* API endpoint yoksa localStorage'dan doldur */
        fallbackFromStorage();
      }
    } catch {
      fallbackFromStorage();
    } finally {
      if (!silent) setFetchLoading(false);
    }
  };

  const fallbackFromStorage = () => {
    /* context'te artık ad/soyad ayrı tutuluyor */
    setAd(customer?.ad ?? "");
    setSoyad(customer?.soyad ?? "");
    setEposta(customer?.email ?? "");
    setTelefon(customer?.telefon ?? "");

    /* Eğer context'te ad/soyad ayrı yoksa, name'i böl */
    if (!customer?.ad && customer?.name) {
      const parts = customer.name.trim().split(/\s+/);
      setAd(parts[0] ?? "");
      setSoyad(parts.slice(1).join(" ") ?? "");
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id]);

  /* ─── Profil Kaydet ─── */
  const saveProfile = async () => {
    if (!customer?.id) return;
    setProfilLoading(true);
    setProfilMsg(null);
    try {
      const res = await postRequest<any, ApiRes<any>>(
        "/MusteriAuth/profil-guncelle",
        {
          MusteriId: customer.id,
          Ad:      ad.trim(),
          Soyad:   soyad.trim(),
          Eposta:  eposta.trim(),
          Telefon: telefon.trim(),
        }
      );
      if (!res.success) throw new Error(res.errorDetail ?? "Güncelleme başarısız.");

      /* Yeni token dönüyorsa hydrate et */
      if (res.data?.token) setTokenAndUserFromToken(res.data.token);

      /* Context + localStorage güncelle */
      updateCustomerProfile({
        ad:      ad.trim(),
        soyad:   soyad.trim(),
        email:   eposta.trim(),
        telefon: telefon.trim(),
      });

      setProfilMsg({ ok: true, text: "Profil bilgileri başarıyla güncellendi." });
    } catch (e: any) {
      setProfilMsg({ ok: false, text: e?.message ?? "Bir hata oluştu." });
    } finally {
      setProfilLoading(false);
    }
  };

  /* ─── Şifre Değiştir ─── */
  const savePassword = async () => {
    if (!customer?.id) return;
    if (yeniSifre !== yeniSifreTekrar) {
      setSifreMsg({ ok: false, text: "Yeni şifreler eşleşmiyor." });
      return;
    }
    if (yeniSifre.length < 6) {
      setSifreMsg({ ok: false, text: "Yeni şifre en az 6 karakter olmalıdır." });
      return;
    }
    setSifreLoading(true);
    setSifreMsg(null);
    try {
      const res = await postRequest<any, ApiRes<any>>(
        "/MusteriAuth/sifre-degistir",
        {
          MusteriId:   customer.id,
          MevcutSifre: mevcutSifre,
          YeniSifre:   yeniSifre,
        }
      );
      if (!res.success) throw new Error(res.errorDetail ?? "Şifre değiştirme başarısız.");
      setSifreMsg({ ok: true, text: "Şifreniz başarıyla değiştirildi." });
      setMevcutSifre("");
      setYeniSifre("");
      setYeniSifreTekrar("");
    } catch (e: any) {
      setSifreMsg({ ok: false, text: e?.message ?? "Bir hata oluştu." });
    } finally {
      setSifreLoading(false);
    }
  };

  const canSaveProfile  = ad.trim().length > 0 && !profilLoading;
  const canSavePassword = mevcutSifre.length > 0 && yeniSifre.length >= 6 && yeniSifreTekrar.length > 0 && !sifreLoading;

  /* ── İsim baş harfleri ── */
  const initials = [ad[0], soyad[0]].filter(Boolean).join("").toUpperCase() || "M";
  const displayName = [ad, soyad].filter(Boolean).join(" ") || customer?.name || "Müşteri";

  return (
    <div className="space-y-5">

      {/* ── Özet kart ── */}
      <div
        className="rounded-2xl p-4 border flex items-center justify-between gap-4"
        style={{ backgroundColor: S.surface2, borderColor: S.divider, boxShadow: S.softShadow }}
      >
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <div
            className="w-14 h-14 rounded-full grid place-items-center text-xl font-bold shrink-0 shadow"
            style={{ background: S.gradient, color: "#fff" }}
          >
            {fetchLoading ? <Loader2 size={20} className="animate-spin" /> : initials}
          </div>

          <div className="min-w-0">
            <div className="font-semibold truncate" style={{ color: S.textPri }}>
              {displayName}
            </div>
            <div className="text-sm truncate" style={{ color: S.textSec }}>
              {eposta || customer?.email || "—"}
            </div>
            {telefon && (
              <div className="text-xs mt-0.5" style={{ color: S.textSec }}>
                {telefon}
              </div>
            )}
            <div className="text-xs mt-0.5" style={{ color: S.textSec }}>
              Müşteri No: <span style={{ color: S.textPri }}>#{customer?.id}</span>
            </div>
          </div>
        </div>

        {/* Yenile butonu */}
        <button
          onClick={() => fetchProfile()}
          disabled={fetchLoading}
          className="h-9 w-9 rounded-xl border grid place-items-center transition shrink-0"
          style={{ backgroundColor: S.surface1, borderColor: S.divider, color: S.textSec }}
          title="Bilgileri yenile"
        >
          <RefreshCw size={15} className={fetchLoading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* ── Sekmeler ── */}
      <div className="flex gap-1 border-b" style={{ borderColor: S.divider }}>
        {([
          { id: "profil" as TabId, label: "Profil Bilgileri", icon: <UserRound size={15} /> },
          { id: "sifre"  as TabId, label: "Şifre Değiştir",   icon: <KeyRound size={15} /> },
        ]).map((t) => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); setProfilMsg(null); setSifreMsg(null); }}
            className="inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors"
            style={{
              borderBottomColor: tab === t.id ? S.accent : "transparent",
              color: tab === t.id ? S.textPri : S.textSec,
            }}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* ══════════ TAB: PROFİL BİLGİLERİ ══════════ */}
      {tab === "profil" && (
        <div className="space-y-4">

          {fetchLoading && (
            <div className="flex items-center gap-2 text-sm" style={{ color: S.textSec }}>
              <Loader2 size={15} className="animate-spin" /> Profil bilgileri yükleniyor…
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <InputField
              label="Ad"
              value={ad}
              onChange={setAd}
              placeholder="Adınız"
              icon={<UserRound size={16} />}
              required
              disabled={fetchLoading}
              S={S}
            />
            <InputField
              label="Soyad"
              value={soyad}
              onChange={setSoyad}
              placeholder="Soyadınız"
              icon={<UserRound size={16} />}
              disabled={fetchLoading}
              S={S}
            />
            <InputField
              label="E-posta"
              value={eposta}
              onChange={setEposta}
              placeholder="ornek@mail.com"
              type="email"
              icon={<Mail size={16} />}
              disabled={fetchLoading}
              S={S}
            />
            <InputField
              label="Telefon"
              value={telefon}
              onChange={setTelefon}
              placeholder="05xx xxx xx xx"
              type="tel"
              icon={<Phone size={16} />}
              disabled={fetchLoading}
              S={S}
            />
          </div>

          {profilMsg && <Toast ok={profilMsg.ok} text={profilMsg.text} />}

          <button
            onClick={saveProfile}
            disabled={!canSaveProfile || fetchLoading}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition active:scale-[.98]"
            style={{ background: S.gradient }}
          >
            {profilLoading
              ? <><Loader2 size={16} className="animate-spin" /> Kaydediliyor…</>
              : <><Save size={16} /> Kaydet</>
            }
          </button>
        </div>
      )}

      {/* ══════════ TAB: ŞİFRE DEĞİŞTİR ══════════ */}
      {tab === "sifre" && (
        <div className="space-y-4 max-w-sm">

          <InputField
            label="Mevcut Şifre"
            value={mevcutSifre}
            onChange={setMevcutSifre}
            placeholder="••••••••"
            type={showMevcut ? "text" : "password"}
            icon={<KeyRound size={16} />}
            required
            S={S}
            rightElement={
              <button type="button" onClick={() => setShowMevcut((v) => !v)} style={{ color: S.textSec }}>
                {showMevcut ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            }
          />

          <InputField
            label="Yeni Şifre"
            value={yeniSifre}
            onChange={setYeniSifre}
            placeholder="En az 6 karakter"
            type={showYeni ? "text" : "password"}
            icon={<KeyRound size={16} />}
            required
            S={S}
            rightElement={
              <button type="button" onClick={() => setShowYeni((v) => !v)} style={{ color: S.textSec }}>
                {showYeni ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            }
          />

          <PasswordStrength password={yeniSifre} S={S} />

          <InputField
            label="Yeni Şifre Tekrar"
            value={yeniSifreTekrar}
            onChange={setYeniSifreTekrar}
            placeholder="Şifreyi tekrar girin"
            type="password"
            icon={<KeyRound size={16} />}
            required
            S={S}
          />

          {yeniSifreTekrar.length > 0 && yeniSifre !== yeniSifreTekrar && (
            <p className="text-xs" style={{ color: "#ef4444" }}>Şifreler eşleşmiyor.</p>
          )}

          {sifreMsg && <Toast ok={sifreMsg.ok} text={sifreMsg.text} />}

          <button
            onClick={savePassword}
            disabled={!canSavePassword}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition active:scale-[.98]"
            style={{ background: S.gradient }}
          >
            {sifreLoading
              ? <><Loader2 size={16} className="animate-spin" /> Değiştiriliyor…</>
              : <><KeyRound size={16} /> Şifreyi Değiştir</>
            }
          </button>

          <p className="text-xs" style={{ color: S.textSec }}>
            Güvenliğiniz için şifrenizi düzenli aralıklarla güncelleyin.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
